import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    userRole: null,
    userID: null,
    isLoading: true, // Only for initial load
  });

  const checkAuth = async () => {
    try {
      const authStatusString = localStorage.getItem('authStatus');
      
      if (!authStatusString) {
        return { isAuthenticated: false, userRole: null, userID: null };
      }

      const authStatus = JSON.parse(authStatusString);
      // Ensure the parsed data has the expected structure
      if (
        authStatus.isLoggedIn &&
        authStatus.user?.type &&
        authStatus.user?.id
      ) {
        return {
          isAuthenticated: authStatus.isLoggedIn,
          userRole: authStatus.user.type, // Match the structure used in setLogin
          userID: authStatus.user.id,
        };
      }
      
      return { isAuthenticated: false, userRole: null, userID: null };
    } catch (err) {
      console.error('Auth check error:', err);
      return { isAuthenticated: false, userRole: null, userID: null };
    }
  };

  // Initialize auth state only once
  useEffect(() => {
    const initializeAuth = async () => {
      const { isAuthenticated, userRole, userID } = await checkAuth();
      setAuthState({
        isAuthenticated,
        userRole,
        userID,
        isLoading: false
      });
    };
    
    initializeAuth();
  }, []); // Empty dependency array ensures this runs only once

  const setLogin = async (endpoint, credentials) => {
  try {
    const formData = new FormData();
    for (const key in credentials) {
      if (credentials.hasOwnProperty(key)) {
        formData.append(key, credentials[key]);
      }
    }
    formData.append('action', 'userLogin');
    formData.append('token', process.env.REACT_APP_API_TOKEN);

    const response = await axios.post(
      `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      }
    );

    console.log(response);
    

    if (response.data?.success) {
      const userRole = response.data.data?.user_role;
      const userID = response.data.data?.user_id;
      localStorage.setItem('authStatus', JSON.stringify({
        isLoggedIn: true,
        user: {
          type: userRole, // Use 'type' to match Login component
          id: userID,
        },
      }));
      
      setAuthState({
        isAuthenticated: true,
        userRole,
        userID,
        isLoading: false,
      });
      
      return { success: true, data: { userRole, userID } };
    }
    return {
      success: false,
      message: response.data?.message || 'Unknown error',
      errors: response.data?.errors,
    };
  } catch (err) {
    console.error('Error logging in:', err);
    return {
      success: false,
      message: err.response?.data?.message || 'Network error',
      status: err.response?.status,
    };
  }
};

  const logout = async () => {
    try {
    //   // Optionally, make an API call to invalidate the session
    //   const formData = new FormData();
    //   formData.append('action', 'userLogout');
    //   formData.append('token', process.env.REACT_APP_API_TOKEN);

    //   await axios.post(
    //     'https://mindsinaction.com.na/api/mindsbackend/logout',
    //     formData,
    //     {
    //       headers: { 'Content-Type': 'multipart/form-data' },
    //       withCredentials: true,
    //     }
    //   );

      // Clear localStorage and reset auth state
      localStorage.removeItem('authStatus');
      setAuthState({
        isAuthenticated: false,
        userRole: null,
        isLoading: false
      });

      return {
        success: true,
        message: 'Logged out successfully',
      };
    } catch (err) {
      console.error('Error logging out:', err);
      // Clear auth state even if API call fails
      localStorage.removeItem('authStatus');
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
      });

      return {
        success: false,
        message:  'Logout failed',
      };
    }
  };

  const addData = async (endpoint, tableName, credentials) => {
    try {
      const formData = new FormData();
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }
      formData.append('action', 'addDatas');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: false,
        message: response.data?.message || 'Unknown error',
        errors: response.data?.errors,
      };
    } catch (err) {
      console.error(`Error adding ${tableName} Data:`, err);
      return {
        success: false,
        message: err.response?.data?.message || 'Network error',
        status: err.response?.status,
      };
    }
  };

  const updateData = async (endpoint, tableName, credentials, id) => {
    try {
      const formData = new FormData();
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }
      formData.append('action', 'updateDatas');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      formData.append('id', id);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: false,
        message: response.data?.message || 'Unknown error',
        errors: response.data?.errors,
      };
    } catch (err) {
      console.error(`Error updating ${tableName} Data:`, err);
      return {
        success: false,
        message: err.response?.data?.message || 'Network error',
        status: err.response?.status,
      };
    }
  };

  const getData = async (endpoint, tableName, params = {}) => {
    try {
      const formData = new FormData();
      formData.append('action', 'getDatas');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      Object.keys(params).forEach((key) => {
        formData.append(key, params[key]);
      });

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'API rejected request');
      }

      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message,
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Request failed',
        errors: err.response?.data?.errors || [],
      };
    }
  };

  const deleteData = async (endpoint, tableName, id) => {
    try {
      const formData = new FormData();
      formData.append('action', 'deleteData');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      formData.append('id', id);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: false,
        message: response.data?.message || 'Unknown error',
        errors: response.data?.errors,
      };
    } catch (err) {
      console.error(`Error deleting ${tableName}:`, err);
      return {
        success: false,
        message: err.response?.data?.message || 'Network error',
        status: err.response?.status,
      };
    }
  };

  const sendMails = async (endpoint, credentials) => {
    try {
      const formData = new FormData();
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }
      formData.append('action', 'sendMails');
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: false,
        message: response.data?.message || 'Unknown error',
        errors: response.data?.errors,
      };
    } catch (err) {
      console.error(`Error sending mail:`, err);
      return {
        success: false,
        message: err.response?.data?.message || 'Network error',
        status: err.response?.status,
      };
    }
  };

  const sendRegistrationEmail = async (endpoint, credentials) => {
    try {
      const formData = new FormData();
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      formData.append('action', 'registrationNotification');
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      }
      return {
        success: false,
        message: response.data?.message || 'Unknown error',
        errors: response.data?.errors,
      };
    } catch (err) {
      console.error(`Error sending registration email:`, err);
      return {
        success: false,
        message: err.response?.data?.message || 'Network error',
        status: err.response?.status,
      };
    }
  };

  return (
    <AuthContext.Provider value={{ ...authState, checkAuth, addData, getData, updateData, sendRegistrationEmail, deleteData, setLogin, logout, sendMails }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);